import { useEffect, useRef } from 'react';

/**
 * How many blank rows a fresh stock-entry form opens with, so a paper invoice
 * can be typed straight down the grid instead of one "+ Add item" per line.
 */
export const ENTRY_ROWS = 8;

/** Tops a line list up to `min` blank rows, leaving a longer list alone. */
export function padRows<T>(rows: T[], make: () => T, min = ENTRY_ROWS): T[] {
  if (rows.length >= min) return rows;
  return [...rows, ...Array.from({ length: min - rows.length }, () => make())];
}

/**
 * Removing lines from a grid that keeps at least `min` rows on screen.
 *
 * Taking a line out of a grid at its minimum puts a blank one back at the
 * bottom, so the form never shrinks below the rows it opened with; a longer
 * grid just loses the line (16 → 15). A blank row that would only be put
 * straight back has no ✕ at all, rather than one that does nothing.
 *
 * Focus moves to the ✕ of the row that slid into the removed one's place (or
 * the nearest one above it), so a keyboard user can keep clearing lines
 * instead of being dropped back at the top of the page.
 */
export function useLineRemoval<T extends { key: string }>(
  lines: T[],
  onChange: (lines: T[]) => void,
  make: () => T,
  isBlank: (line: T) => boolean,
  min = 1,
) {
  const floor = Math.max(min, 1);
  const buttons = useRef(new Map<string, HTMLButtonElement | null>());
  const refocusAt = useRef<number | null>(null);

  useEffect(() => {
    const at = refocusAt.current;
    if (at === null) return;
    refocusAt.current = null;
    for (let i = Math.min(at, lines.length - 1); i >= 0; i -= 1) {
      const button = buttons.current.get(lines[i]!.key);
      if (button) {
        button.focus();
        return;
      }
    }
  }, [lines]);

  return {
    canRemove: (line: T) => lines.length > floor || !isBlank(line),
    remove(key: string) {
      refocusAt.current = lines.findIndex((l) => l.key === key);
      onChange(padRows(lines.filter((l) => l.key !== key), make, floor));
    },
    buttonRef: (key: string) => (el: HTMLButtonElement | null) => {
      buttons.current.set(key, el);
    },
  };
}
